import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./WorkerSelectorPage.module.css"; // We will update this CSS file next

const WorkerSelectorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    allWorkers = [],
    initialSelectedIds = [],
    returnPath = "/",
    preservedState,
  } = location.state || {};

  const [currentSelection, setCurrentSelection] = useState(initialSelectedIds);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredWorkers = useMemo(
    () =>
      allWorkers.filter((worker) =>
        worker.username.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [allWorkers, searchTerm]
  );

  // NEW: Handler for the 'Select All' / 'Deselect All' button
  const handleSelectAll = () => {
    const filteredIds = filteredWorkers.map((w) => w.id);
    const allFilteredSelected =
      filteredIds.length > 0 &&
      filteredIds.every((id) => currentSelection.includes(id));

    if (allFilteredSelected) {
      // If all are selected, deselect them
      setCurrentSelection(
        currentSelection.filter((id) => !filteredIds.includes(id))
      );
    } else {
      // If not all are selected, select all filtered items
      setCurrentSelection([...new Set([...currentSelection, ...filteredIds])]);
    }
  };

  // UPDATED: Renamed for consistency, but logic is the same
  const handleSelect = (workerId, isChecked) => {
    setCurrentSelection(
      isChecked
        ? [...currentSelection, workerId]
        : currentSelection.filter((id) => id !== workerId)
    );
  };

  const handleConfirm = () => {
    navigate(returnPath, {
      state: {
        preservedState,
        workerSelection: currentSelection,
      },
    });
  };

  const handleGoBack = () => {
    navigate(returnPath, {
      state: {
        preservedState,
        workerSelection: initialSelectedIds,
      },
    });
  };

  // NEW: Derived state to determine the 'Select All' button text
  const allFilteredSelected =
    filteredWorkers.length > 0 &&
    filteredWorkers.every((w) => currentSelection.includes(w.id));

  return (
    <div className={styles.pageContainer}>
      <header className={styles.pageHeader}>
        <button onClick={handleGoBack} className={styles.backButton}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24"
            fill="currentColor"
          >
            <path d="M10.8284 12.0007L15.7782 16.9504L14.364 18.3646L8 12.0007L14.364 5.63672L15.7782 7.05093L10.8284 12.0007Z"></path>
          </svg>
        </button>
        <h1>{t("Assign Workers")}</h1>
        <div className={styles.headerPlaceholder}></div>
      </header>

      <main className={styles.pageContent}>
        <div className={styles.searchWrapper}>
          <svg
            className={styles.searchIcon}
            viewBox="0 0 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("Search by worker name...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* NEW: Select All Control section */}
        <div className={styles.selectAllControl}>
          <button
            type="button"
            className={styles.selectAllButton}
            onClick={handleSelectAll}
          >
            {allFilteredSelected ? t("Deselect All") : t("Select All")}
          </button>
        </div>

        {/* UPDATED: List structure now matches FieldSelector */}
        <div className={styles.selectorListItems}>
          {filteredWorkers.length > 0 ? (
            filteredWorkers.map((worker) => {
              const workerId = worker.id;
              const isSelected = currentSelection.includes(workerId);
              return (
                <label key={workerId} className={styles.selectorItemRow}>
                  <div className={styles.itemDetails}>
                    <span className={styles.itemName}>{worker.username}</span>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleSelect(workerId, e.target.checked)}
                    />
                    <span className={styles.customCheckbox}></span>
                  </div>
                </label>
              );
            })
          ) : (
            <div className={styles.noResults}>
              <p>{t("No unassigned workers found.")}</p>
            </div>
          )}
        </div>
      </main>

      <footer className={styles.pageFooter}>
        <button className={styles.confirmButton} onClick={handleConfirm}>
          {t("Continue")} ({currentSelection.length})
        </button>
      </footer>
    </div>
  );
};

export default WorkerSelectorPage;
